import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, CardContent, CircularProgress } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { useGetMyProfileQuery } from '../../../../../services/ProfileService';
import type { CenterAccessView } from '../../../../../services/ProfileService/types/profileTypes';
import { CenterSelector } from '../../../../common/components/CenterSelector';
import { useCenterSelection } from '../../../../common/hooks/useCenterSelection';
import { PageCard, PageContainer, PageHeader } from '../../../../common/components/styled-ui-elements';
import { useMedicalRecordsQueue, useMedicalRecordsRequest } from '../hooks/useMedicalRecords';
import MedicalRecordsSearchPanel from '../components/MedicalRecordsSearchPanel';
import MedicalRecordsQueueTable from '../components/MedicalRecordsQueueTable';
import { MedicalRecordsDemoRequest } from '../components/MedicalRecordsDocuments';
import type { MedicalRecordsSort } from '../data/medicalRecordsViews';

const getAuthorizedCenters = (centers: CenterAccessView[]): CenterAccessView[] =>
  centers.filter((entry) => !entry.accessUntil || new Date(entry.accessUntil) >= new Date());

const useMedicalRecordsScope = () => {
  const [searchParams] = useSearchParams();
  const { data: profile, isLoading: isProfileLoading, isError: isProfileError } = useGetMyProfileQuery();
  const company = profile?.organization ?? '';
  const center = searchParams.get('center') || '';
  const authorizedCenters = getAuthorizedCenters(profile?.centerAccess ?? []);
  const authorizedCenter = authorizedCenters.some((entry) => entry.code === center) ? center : '';
  const { handleCenterChange } = useCenterSelection();

  return { company, center, authorizedCenter, authorizedCenters, isProfileLoading, isProfileError, handleCenterChange };
};

type MedicalRecordsPageFrameProps = {
  title: string;
  subtitle: string;
  scope: ReturnType<typeof useMedicalRecordsScope>;
  children: React.ReactNode;
};

const MedicalRecordsPageFrame: React.FC<MedicalRecordsPageFrameProps> = ({ title, subtitle, scope, children }) => (
  <PageContainer
    data-testid="medical-records-page-container"
    sx={{ flex: '1 1 100%', flexGrow: 1, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
  >
    <Box sx={{ display: 'flex', flex: '1 1 100%', width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <PageCard sx={{ flex: '1 1 100%', width: '100%', minWidth: 0, maxWidth: '100%' }} data-testid="medical-records-page-card">
        <PageHeader
          icon={DescriptionIcon}
          title={title}
          subtitle={subtitle}
          action={<CenterSelector value={scope.center} onChange={scope.handleCenterChange} options={scope.authorizedCenters} autoSelect={false} />}
        />
        <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
          {scope.isProfileLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress data-testid="medical-records-profile-loading" />
            </Box>
          ) : scope.isProfileError ? (
            <Alert severity="error" data-testid="medical-records-profile-error">
              Unable to load your center access. Please try again.
            </Alert>
          ) : !scope.authorizedCenter ? (
            <Alert severity="info" data-testid="medical-records-select-center">
              Select a center to continue.
            </Alert>
          ) : children}
        </CardContent>
      </PageCard>
    </Box>
  </PageContainer>
);

export const MedicalRecordsRequestPage: React.FC = () => {
  const scope = useMedicalRecordsScope();
  const [sort, setSort] = useState<MedicalRecordsSort | undefined>();

  const {
    searchTerm,
    setSearchTerm,
    submitSearch,
    clearSearch,
    canClearSearch,
    searchState,
    submissionState,
    requestDocuments,
    showPreviousSearchPage,
    showNextSearchPage,
    isRequesting,
    activeRequestedEncounterIds,
    clearSubmissionFeedback,
  } = useMedicalRecordsRequest(scope.company, scope.authorizedCenter, sort);

  return (
    <MedicalRecordsPageFrame title="Request Documents" subtitle="Find an encounter and request its records" scope={scope}>
      <MedicalRecordsDemoRequest company={scope.company} centerId={scope.authorizedCenter} />
      <MedicalRecordsSearchPanel
        onDismissFeedback={clearSubmissionFeedback}
        sort={sort ?? { field: 'created', descending: true }}
        onSortChange={setSort}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        onSubmit={submitSearch}
        onClear={clearSearch}
        canClear={canClearSearch}
        state={searchState}
        onRequest={requestDocuments}
        activeRequestedEncounterIds={activeRequestedEncounterIds}
        isRequesting={isRequesting}
        submissionState={submissionState}
        onPreviousPage={showPreviousSearchPage}
        onNextPage={showNextSearchPage}
      />
    </MedicalRecordsPageFrame>
  );
};

export const MedicalRecordsRequestsPage: React.FC = () => {
  const scope = useMedicalRecordsScope();
  const [sort, setSort] = useState<MedicalRecordsSort | undefined>();
  const { queueState, showPreviousPage, showNextPage } = useMedicalRecordsQueue(
    scope.company, scope.authorizedCenter, sort,
  );

  return (
    <MedicalRecordsPageFrame title="My Requests" subtitle="Track your Medical Records requests" scope={scope}>
      <MedicalRecordsQueueTable key={`${scope.company}:${scope.authorizedCenter}`} state={queueState} onPreviousPage={showPreviousPage} onNextPage={showNextPage}
        sort={sort ?? { field: 'submitted', descending: true }} onSortChange={setSort} />
    </MedicalRecordsPageFrame>
  );
};

export default MedicalRecordsRequestPage;
